<template>
  <div class="form-group" :class="{ 'form-group--error': errors }">
    <label :for="field" v-if="label && !isSingleCheckbox">{{ label }}</label>

    <input
      v-if="type === 'url'"
      type="url"
      :id="field"
      v-model="inputVal"
      @input="validateURL"
      :readonly="readonly"
    />

    <input
      v-else-if="type === 'tel'"
      type="tel"
      :id="field"
      v-model="inputVal"
      @input="validateTel"
      :readonly="readonly"
    />

    <div
      class="select"
      :class="{ 'select--icon': selectedIcon }"
      v-else-if="type === 'select'"
    >
      <select :id="field" v-model="inputVal" :disabled="readonly">
        <option value="" v-if="showEmpty"></option>
        <option
          v-for="option in normalizedOptions"
          :value="option.value"
          :key="option.value"
        >
          {{ option.name }}
        </option>
      </select>

      <FontAwesomeIcon v-if="selectedIcon" :icon="selectedIcon" class="icon" />

      <FontAwesomeIcon class="drop" :icon="down" v-if="!readonly" />
    </div>

    <textarea
      v-else-if="type === 'textarea'"
      :maxlength="maxLength"
      :id="field"
      v-model="inputVal"
      @keyup="textAreaAdjust"
      :readonly="readonly"
    ></textarea>

    <div
      v-else-if="type === 'number'"
      class="input-wrapper"
      :class="{ 'input-wrapper--prefixed': prefix }"
    >
      <span v-if="prefix" class="input-wrapper__prefix">{{ prefix }}</span>
      <input
        type="number"
        :id="field"
        v-model="inputVal"
        :readonly="readonly"
        :min="min"
        :max="max"
        :step="step"
      />
    </div>

    <div
      v-else-if="type === 'checkbox' && options.length"
      class="checkbox-group"
    >
      <label
        v-for="option in normalizedOptions"
        :key="option.value"
        class="checkbox-group__option"
      >
        <input
          type="checkbox"
          :value="option.value"
          :checked="isChecked(option.value)"
          :disabled="readonly"
          @change="toggleCheckbox(option.value, $event.target.checked)"
        />
        {{ option.name }}
      </label>
    </div>

    <div v-else-if="type === 'checkbox'" class="checkbox">
      <label :for="field">
        <input
          type="checkbox"
          :id="field"
          :checked="!!inputVal"
          :disabled="readonly"
          @change="inputVal = $event.target.checked"
        />
        {{ label }}
      </label>
    </div>

    <div v-else-if="type === 'radio'" class="radio-group">
      <label
        v-for="option in normalizedOptions"
        :key="option.value"
        class="radio-group__option"
      >
        <input
          type="radio"
          :name="field"
          :value="option.value"
          :checked="inputVal === option.value"
          :disabled="readonly"
          @change="inputVal = option.value"
        />
        {{ option.name }}
      </label>
    </div>

    <div
      v-else
      class="input-wrapper"
      :class="{ 'input-wrapper--prefixed': prefix }"
    >
      <span v-if="prefix" class="input-wrapper__prefix">{{ prefix }}</span>
      <input
        type="text"
        :id="field"
        v-model="inputVal"
        :readonly="readonly"
        @input="validateRequired"
      />
    </div>

    <div v-if="helpText" class="help-text">{{ helpText }}</div>

    <div v-if="errors" class="error-message">{{ errors }}</div>
  </div>
</template>

<script>
import { faCaretDown } from '@fortawesome/free-solid-svg-icons';

export default {
  name: 'FormGroup',
  props: {
    label: {
      type: String,
    },
    type: {
      type: String,
      default: 'text',
    },
    helperText: {
      type: String,
      default: '',
    },
    value: {
      type: [String, Number, Boolean, Array],
      default: '',
    },
    errors: {
      type: String,
      default: '',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array,
      default: () => [],
    },
    fieldName: {
      type: String,
      default: '',
    },
    showEmpty: {
      type: Boolean,
      default: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    maxLength: {
      type: Number,
      default: 2200,
    },
    prefix: {
      type: String,
      default: '',
    },
    min: {
      type: [Number, String],
      default: undefined,
    },
    max: {
      type: [Number, String],
      default: undefined,
    },
    step: {
      type: [Number, String],
      default: undefined,
    },
  },
  computed: {
    inputVal: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    field() {
      return this.fieldName || this.label.toLowerCase().replace(' ', '_');
    },
    isSingleCheckbox() {
      return this.type === 'checkbox' && !this.options.length;
    },
    normalizedOptions() {
      return this.options.map((opt) =>
        opt !== null && typeof opt === 'object' ? opt : { value: opt, name: opt }
      );
    },
    selectedIcon() {
      const selectedOption = this.normalizedOptions.find(
        (option) => option.value === this.inputVal
      );
      if (selectedOption && selectedOption.icon) {
        return selectedOption.icon;
      } else {
        return false;
      }
    },
    helpText() {
      if (this.helperText) {
        return this.helperText;
      } else if (this.type === 'tel') {
        return 'Format: 000-000-0000';
      } else if (this.type === 'textarea') {
        return this.inputVal.length + ' / ' + this.maxLength + ' characters';
      } else {
        return false;
      }
    },
  },
  data() {
    return {
      down: faCaretDown,
    };
  },
  methods: {
    textAreaAdjust(ev) {
      const element = ev.target;
      element.style.height = '1px';
      element.style.height = 25 + element.scrollHeight + 'px';
    },
    isChecked(value) {
      return Array.isArray(this.inputVal) && this.inputVal.includes(value);
    },
    toggleCheckbox(value, checked) {
      const current = Array.isArray(this.inputVal) ? [...this.inputVal] : [];
      const idx = current.indexOf(value);

      if (checked && idx === -1) {
        current.push(value);
      } else if (!checked && idx > -1) {
        current.splice(idx, 1);
      }

      this.inputVal = current;
    },
    validateRequired(ev) {
      const element = ev.target;

      this.$emit('errors', [this.field, false]);

      if (this.required && element.value.length === 0) {
        this.$emit('errors', [this.field, 'This field is required.']);
      }
    },
    validateURL(ev) {
      const element = ev.target;

      if (this.required) {
        this.validateRequired(ev);
      }

      this.$emit('errors', [this.field, false]);

      if (element.value.length === 0) {
        return;
      }

      if (
        element.value.length > 8 &&
        !element.value.startsWith('http://') &&
        !element.value.startsWith('https://')
      ) {
        this.inputVal = 'https://' + element.value;
      }

      if (
        !element.value.match(
          /(http(s)?:\/\/.)?([A-Za-z0-9-]+\.)+[A-Za-z]{2,}\/?/
        )
      ) {
        this.$emit('errors', [this.field, 'Please enter a valid URL.']);
      }
    },
    validateTel(ev) {
      const element = ev.target;

      this.$emit('errors', [this.field, false]);

      if (element.value.length === 0) {
        return;
      }

      const phoneNumber = element.value.replace(/[^\d]/g, ''); // Remove non-digit characters
      let formattedPhoneNumber = phoneNumber.replace(
        /(\d{3})(\d{3})(\d{4})/,
        '$1-$2-$3'
      ); // Format as 000-000-0000

      if (phoneNumber.length > 10) {
        const extension = phoneNumber.slice(10);
        formattedPhoneNumber = formattedPhoneNumber.slice(0, 12);
        formattedPhoneNumber += ` ext ${extension}`;
      }

      this.inputVal = formattedPhoneNumber;

      if (
        !formattedPhoneNumber.match(/(^[\d-]{12}$|^[\d-]{12} ext \d{2,4}$)/)
      ) {
        this.$emit('errors', [
          this.field,
          'Please enter a valid phone number.',
        ]);
      }
    },
  },
};
</script>
